import { create } from "zustand";
import { dbAll, dbDelete, dbPut } from "../storage/db.js";
import { activeProfileId, useProfiles } from "../settings/profileStore.js";

/**
 * Per-channel cross-provider mirrors (Section 16, TiviMate-style).
 *
 * A mirror is an EXPLICIT, user-created link: "this channel in provider A is
 * also available in provider B — if A's stream fails, try B's." It is never
 * the result of automatic catalog merging; the user links two channels on
 * purpose, so the guide stays source-separated (which matches mainstream
 * players) while still giving a concrete fallback when one account/upstream
 * is down.
 *
 * The mirror stores the fallback's provider id, channel id/name and provider-
 * native URL, but not credentials. At resolve time the app reads the current
 * provider config, so password rotations stay authoritative. Rows are scoped
 * to the active local profile, just like favorites.
 */

/** A fallback stream on another provider. */
export interface MirrorFallback {
  /** Target provider id — same id the settings `providers` use, and the
   *  sourceId the API caches/stream-tokens are scoped by. */
  providerId: string;
  /** Provider kind at link time; resolves credentials by matching a current
   *  provider with this id in settings. */
  providerKind: "xtream" | "m3u";
  /** The target provider's native channel id. */
  channelId: string;
  /** Display name for the fallback (the target provider's channel name). */
  channelName: string;
  /** Provider-native stream URL for the fallback. May contain redacted
   *  placeholders; the stream-token step swaps in the current credentials. */
  streamUrl: string;
}

/** A mirror: one catalog channel linked to one or more cross-provider
 *  fallbacks. Immutable-ish — mutation happens by replacing the record. */
export interface ChannelMirror {
  /** `${sourceId}:${channelId}` of the channel being mirrored. */
  id: string;
  profileId: string;
  sourceId: string;
  channelId: string;
  channelName: string;
  channelNumber?: number;
  fallbacks: MirrorFallback[];
  updatedAt: string;
}

const mirrorKey = (sourceId: string, channelId: string) => `${sourceId}:${channelId}`;
const profileMirrorKey = (profileId: string, id: string) => `${profileId}:${id}`;

let hydrationGeneration = 0;
let hydrationPromise: { profileId: string; promise: Promise<void> } | null = null;

interface MirrorState {
  /** Keyed by `${sourceId}:${channelId}` in the active profile's view. */
  mirrors: Record<string, ChannelMirror>;
  /** False until the active profile's IndexedDB read completes. */
  hydrated: boolean;

  hydrate: () => Promise<void>;
  resetForProfileSwitch: () => void;
  getMirror: (sourceId: string, channelId: string) => ChannelMirror | undefined;
  fallbacksFor: (sourceId: string, channelId: string) => MirrorFallback[];
  addFallback: (
    sourceId: string,
    channelId: string,
    channelName: string,
    channelNumber: number | undefined,
    fallback: MirrorFallback,
  ) => Promise<void>;
  removeFallback: (sourceId: string, channelId: string, providerId: string, targetChannelId: string) => Promise<void>;
  removeMirror: (sourceId: string, channelId: string) => Promise<void>;
}

export const useMirrorStore = create<MirrorState>()((set, get) => ({
  mirrors: {},
  hydrated: false,

  resetForProfileSwitch: () => {
    hydrationGeneration += 1;
    hydrationPromise = null;
    set({ mirrors: {}, hydrated: false });
    // Reload now so a channel's mirror controls are ready under the new profile.
    // The generation check discards any read that belonged to the old profile.
    void get().hydrate();
  },

  hydrate: async () => {
    if (get().hydrated) return;
    const profileId = activeProfileId();
    if (hydrationPromise?.profileId === profileId) return hydrationPromise.promise;
    const generation = ++hydrationGeneration;
    const active = { profileId, promise: Promise.resolve() as Promise<void> };
    const promise = (async () => {
      const rows = await dbAll<ChannelMirror>("mirrors").catch(() => [] as ChannelMirror[]);
      if (generation !== hydrationGeneration || profileId !== activeProfileId()) return;
      const mirrors: Record<string, ChannelMirror> = {};
      for (const row of rows) {
        if (!row?.id || !row.sourceId || !row.channelId || !Array.isArray(row.fallbacks) || row.profileId !== profileId) continue;
        mirrors[row.id] = row;
      }
      set({ mirrors, hydrated: true });
    })();
    active.promise = promise;
    hydrationPromise = active;
    try {
      await promise;
    } finally {
      if (hydrationPromise === active) hydrationPromise = null;
    }
  },

  getMirror: (sourceId, channelId) => {
    const mirror = get().mirrors[mirrorKey(sourceId, channelId)];
    return mirror?.profileId === activeProfileId() ? mirror : undefined;
  },

  fallbacksFor: (sourceId, channelId) => get().getMirror(sourceId, channelId)?.fallbacks ?? [],

  addFallback: async (sourceId, channelId, channelName, channelNumber, fallback) => {
    const profileId = activeProfileId();
    // Wait only for a first read: once hydrated the change shows at once.
    if (!get().hydrated) {
      await get().hydrate();
      if (profileId !== activeProfileId()) return;
    }
    const id = mirrorKey(sourceId, channelId);
    const existing = get().getMirror(sourceId, channelId);
    const fallbacks = (existing?.fallbacks ?? []).filter(
      (item) => !(item.providerId === fallback.providerId && item.channelId === fallback.channelId),
    );
    const record: ChannelMirror = {
      id,
      profileId,
      sourceId,
      channelId,
      channelName,
      ...(channelNumber !== undefined ? { channelNumber } : {}),
      fallbacks: [...fallbacks, fallback],
      updatedAt: new Date().toISOString(),
    };
    set({ mirrors: { ...get().mirrors, [id]: record } });
    await dbPut("mirrors", record, profileMirrorKey(profileId, id));
  },

  removeFallback: async (sourceId, channelId, providerId, targetChannelId) => {
    const profileId = activeProfileId();
    // Wait only for a first read: once hydrated the change shows at once.
    if (!get().hydrated) {
      await get().hydrate();
      if (profileId !== activeProfileId()) return;
    }
    const id = mirrorKey(sourceId, channelId);
    const existing = get().getMirror(sourceId, channelId);
    if (!existing) return;
    const fallbacks = existing.fallbacks.filter(
      (item) => !(item.providerId === providerId && item.channelId === targetChannelId),
    );
    if (fallbacks.length === 0) {
      await get().removeMirror(sourceId, channelId);
      return;
    }
    const updated = { ...existing, fallbacks, updatedAt: new Date().toISOString() };
    set({ mirrors: { ...get().mirrors, [id]: updated } });
    await dbPut("mirrors", updated, profileMirrorKey(profileId, id));
  },

  removeMirror: async (sourceId, channelId) => {
    const profileId = activeProfileId();
    // Wait only for a first read: once hydrated the change shows at once.
    if (!get().hydrated) {
      await get().hydrate();
      if (profileId !== activeProfileId()) return;
    }
    const id = mirrorKey(sourceId, channelId);
    const next = { ...get().mirrors };
    delete next[id];
    set({ mirrors: next });
    await dbDelete("mirrors", profileMirrorKey(profileId, id));
  },
}));

useProfiles.subscribe((state, previous) => {
  if (state.activeProfileId !== previous.activeProfileId) {
    useMirrorStore.getState().resetForProfileSwitch();
  }
});

void useMirrorStore.getState().hydrate();
